import type { PriceList, Server, ServerType } from "@cloudserver/contracts";

export interface MonthlyEstimate {
  servers: number;
  ipv4: number;
  backups: number;
  total: number;
  /** Servers whose type or region has no price in the catalogue (not counted). */
  unpriced: number;
}

/**
 * What the project's servers cost per month at most, net of VAT: each
 * server's monthly cap in its region, its public IPv4 and its backups
 * (a share of the server price). Traffic above the included amount,
 * snapshots and other resources are not in it — the overview says so.
 */
export function estimateMonthly(
  servers: Server[],
  types: ServerType[],
  prices: PriceList,
): MonthlyEstimate {
  const out: MonthlyEstimate = { servers: 0, ipv4: 0, backups: 0, total: 0, unpriced: 0 };
  const backupShare = Number(prices.backupPercent) / 100;
  for (const server of servers) {
    const price = types
      .find((type) => type.code === server.type.code)
      ?.prices.find((p) => p.region === server.region);
    if (!price) {
      out.unpriced++;
      continue;
    }
    const monthly = Number(price.monthly.net);
    out.servers += monthly;
    if (server.backupWindow !== null) out.backups += monthly * backupShare;
    if (server.publicIpv4) {
      const ip = prices.publicIpv4.find((p) => p.region === server.region);
      if (ip) out.ipv4 += Number(ip.monthly.net);
    }
  }
  out.total = out.servers + out.ipv4 + out.backups;
  return out;
}
